// src/pages/teacher/exams/TeacherTemplateCreate.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card, Row, Col, Form, Button, Badge, Alert, Spinner, ProgressBar,
} from "react-bootstrap";
import {
  collection, doc, addDoc, getDoc, getDocs, query, where, limit,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

/* ---------------- helpers ---------------- */
const lower = (s) => String(s || "").trim().toLowerCase();
const toU = (s) => String(s || "").trim().toUpperCase();
const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map((x) => x.trim()).filter(Boolean);
  return String(raw).split(/[,|/]+/g).map((x) => x.trim()).filter(Boolean);
};

// sensible A–Z fallback for suggested sections
const SECTION_FALLBACK = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

const TEST_TYPE_OPTIONS = [
  { value: "daily_test",         label: "Daily Test" },
  { value: "weekly_test",        label: "Weekly Test" },
  { value: "monthly_test",       label: "Monthly Test" },
  { value: "unit_test",          label: "Unit Test" },
  { value: "sectional_test",     label: "Sectional Test" },
  { value: "part_test",          label: "Part Test" },
  { value: "full_syllabus_test", label: "Full Syllabus Test" },
  { value: "mock_test",          label: "Mock Test" },
  { value: "sample_paper",       label: "Sample Paper" },
  { value: "previous_year_paper",label: "Previous Year Paper" },
  { value: "other",              label: "Other… (custom)" },
];
const testTypeLabel = (value, custom) =>
  value === "other" && custom?.trim()
    ? custom.trim()
    : (TEST_TYPE_OPTIONS.find(o => o.value === value)?.label || "Daily Test");

const DEFAULT_SECTION = (key = "A") => ({
  id: crypto.randomUUID(),
  key, label: key, questionCount: 0,
  marksPerCorrect: 1,
  negativeMarks: 0,
});
const nextSectionKey = (sections) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const used = new Set((sections || []).map((s) => (s.key || "").toUpperCase()));
  for (let i = 0; i < letters.length; i++) if (!used.has(letters[i])) return letters[i];
  return String((sections?.length || 0) + 1);
};
const DEFAULT_SUBJECT_ROW = () => ({
  id: crypto.randomUUID(),
  subjectId: "",
  subjectName: "",
  sections: [DEFAULT_SECTION("A")],
});

/* Merge class/section mapping (teacher + profile) with per-class sections support */
function resolveScope(raw = {}, profile = {}) {
  const uniq = (arr) => Array.from(new Set(arr));

  const classesRaw = [
    ...parseList(raw.classIds || raw.classes || raw.classNames || raw.className),
    ...parseList(profile.classIds || profile.classes || profile.classNames || profile.className),
  ];
  const classIds = uniq(classesRaw).slice(0, 40);

  const flatSections = uniq([
    ...parseList(raw.sections || raw.section).map(toU),
    ...parseList(profile.sections || profile.section).map(toU),
  ]);

  const sbcRaw  = (raw.sectionsByClass && typeof raw.sectionsByClass === "object") ? raw.sectionsByClass : {};
  const sbcProf = (profile.sectionsByClass && typeof profile.sectionsByClass === "object") ? profile.sectionsByClass : {};

  const sectionsByClass = {};
  classIds.forEach((c) => {
    const a = parseList(sbcRaw[c]).map(toU);
    const b = parseList(sbcProf[c]).map(toU);
    const merged = (a.length || b.length) ? uniq([...a, ...b]) : flatSections;
    sectionsByClass[c] = merged;
  });

  const sectionIds = uniq(Object.values(sectionsByClass).flat().length ? Object.values(sectionsByClass).flat() : flatSections);

  return { classIds, sectionIds, sectionsByClass };
}

/* ---------------- defaults ---------------- */
const DEFAULT_FORM = {
  name: "",
  examType: "objective",
  testType: "daily_test",
  testTypeCustom: "",
  totalQuestions: 0,
  subjectWise: [DEFAULT_SUBJECT_ROW()],
  allowNegativeMarking: true,
  // behavior (objective defaults)
  randomizeQuestions: true,
  allowBackNavigation: true,
  showMarksPerQuestion: true,
  allowedDevices: ["web"],
  instructions: "",
  status: "draft",
  // descriptive extras
  pdfMaxSizeMB: 5,
  pdfInstructions: "",
};

/* ======================================================= */

export default function TeacherTemplateCreate() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, profile } = useAuth();
  const { schoolId, school, loading: schoolLoading } = useSchool();

  const [teacher, setTeacher] = useState(null);
  const [tLoading, setTLoading] = useState(true);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // incoming mapping (preferred) from Exams page
  const incomingClasses = Array.isArray(loc.state?.allowedClasses) ? loc.state.allowedClasses : null;
  const incomingSectionsByClass = (loc.state?.sectionsByClass && typeof loc.state.sectionsByClass === "object")
    ? loc.state.sectionsByClass
    : null;

  /* ---------------- load teacher doc for fallback mapping ---------------- */
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!schoolId || !user?.uid) { setTeacher(null); setTLoading(false); return; }
      setTLoading(true);
      try {
        if (profile?.teacherId) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", profile.teacherId));
          if (!cancelled && snap.exists()) { setTeacher({ id: snap.id, ...(snap.data()||{}) }); setTLoading(false); return; }
        }
        const email = lower(user?.email || "");
        if (email) {
          const qs = await getDocs(query(
            collection(db, "schools", schoolId, "teachers"),
            where("email", "==", email),
            limit(1)
          ));
          if (!cancelled && !qs.empty) {
            const d = qs.docs[0]; setTeacher({ id: d.id, ...(d.data()||{}) }); setTLoading(false); return;
          }
        }
        setTeacher(null);
      } catch { setTeacher(null); }
      if (!cancelled) setTLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, [schoolId, user?.uid, user?.email, profile?.teacherId]);

  const scopeRaw = useMemo(() => resolveScope(teacher || {}, profile || {}), [teacher, profile]);

  // available classes & sections for UI (prefer incoming from Exams)
  const availableClasses = useMemo(
    () => (incomingClasses && incomingClasses.length ? incomingClasses : scopeRaw.classIds || []),
    [incomingClasses, scopeRaw.classIds]
  );
  const availableSectionsByClass = useMemo(() => {
    if (incomingSectionsByClass) return incomingSectionsByClass;
    return scopeRaw.sectionsByClass || {};
  }, [incomingSectionsByClass, scopeRaw.sectionsByClass]);

  // selection state (multi class & per-class sections)
  const [selClasses, setSelClasses] = useState([]);
  const [selSectionsByClass, setSelSectionsByClass] = useState({}); // { [classId]: [secKey,...] } — empty array => "all"

  // initialize defaults: preselect all mapped classes; no sections = "all"
  useEffect(() => {
    setSelClasses(availableClasses || []);
    setSelSectionsByClass({});
  }, [availableClasses]);

  const isClassSelected = (c) => selClasses.includes(c);
  const toggleClass = (c) => {
    setSelClasses((prev) => {
      if (prev.includes(c)) {
        const next = prev.filter((x) => x !== c);
        setSelSectionsByClass((m) => {
          const copy = { ...m }; delete copy[c]; return copy;
        });
        return next;
      }
      return [...prev, c];
    });
  };

  const classSectionPool = (c) => {
    const list = availableSectionsByClass?.[c];
    return (Array.isArray(list) && list.length ? list.map(toU) : SECTION_FALLBACK);
  };

  const isSectionSelected = (c, s) => Array.isArray(selSectionsByClass?.[c]) && selSectionsByClass[c].includes(toU(s));

  const toggleSection = (c, s) => {
    const key = toU(s);
    setSelSectionsByClass((m) => {
      const cur = Array.isArray(m[c]) ? m[c] : [];
      const has = cur.includes(key);
      const next = has ? cur.filter((x) => x !== key) : [...cur, key];
      return { ...m, [c]: next };
    });
  };

  const clearSections = (c) => {
    // empty array means "All sections" (unrestricted)
    setSelSectionsByClass((m) => ({ ...m, [c]: [] }));
  };

  /* ------------ totals & validation ------------- */
  const subjectSum = useMemo(() => {
    return (form.subjectWise || []).reduce((acc, subj) => {
      const secSum = (subj.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
      return acc + secSum;
    }, 0);
  }, [form.subjectWise]);

  const ratio =
    +form.totalQuestions > 0
      ? Math.min(100, Math.round((subjectSum / +form.totalQuestions) * 100))
      : 0;

  const isActiveSchool = useMemo(() => {
    if (!school) return false;
    if (typeof school.active === "boolean") return school.active;
    const status = String(school.status || "").trim().toLowerCase();
    return status === "active";
  }, [school]);

  const canSave = useMemo(() => {
    if (!isActiveSchool) return false;
    if (!form.name?.trim()) return false;
    const total = Number(form.totalQuestions);
    if (!(Number.isFinite(total) && total > 0)) return false;
    if (subjectSum !== total) return false;
    if ((form.subjectWise || []).length === 0) return false;
    if (!selClasses.length) return false;
    return true;
  }, [form, subjectSum, isActiveSchool, selClasses.length]);

  /* ------------- form mutators ------------- */
  const addSubjectRow = () =>
    setForm((f) => ({ ...f, subjectWise: [...(f.subjectWise || []), DEFAULT_SUBJECT_ROW()] }));
  const removeSubjectRow = (id) =>
    setForm((f) => ({ ...f, subjectWise: (f.subjectWise || []).filter((r) => r.id !== id) }));
  const updateSubjectRow = (id, patch) =>
    setForm((f) => ({ ...f, subjectWise: (f.subjectWise || []).map((r) => (r.id === id ? { ...r, ...patch } : r)) }));

  const addSection = (subjectId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const key = nextSectionKey(s.sections || []);
        return { ...s, sections: [...(s.sections || []), DEFAULT_SECTION(key)] };
      });
      return { ...f, subjectWise };
    });
  };
  const removeSection = (subjectId, sectionId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const next = (s.sections || []).filter((sec) => sec.id !== sectionId);
        return { ...s, sections: next.length ? next : [DEFAULT_SECTION("A")] };
      });
      return { ...f, subjectWise };
    });
  };
  const updateSection = (subjectId, sectionId, patch) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const nextSecs = (s.sections || []).map((sec) =>
          sec.id === sectionId ? { ...sec, ...patch } : sec
        );
        return { ...s, sections: nextSecs };
      });
      return { ...f, subjectWise };
    });
  };

  /* ---------------- save ---------------- */
  const doCreate = async (goToQuestions = false) => {
    if (!schoolId) { alert("School not selected."); return; }
    if (!canSave) { alert("Complete required fields and fix validation errors."); return; }

    const isDescriptive = form.examType === "descriptive";

    const normalizedSubjects = (form.subjectWise || []).map((s) => {
      const sections = (s.sections || []).map((sec) => ({
        id: sec.id,
        key: toU(sec.key || ""),
        label: sec.label || (sec.key || ""),
        questionCount: +sec.questionCount || 0,
        marksPerCorrect: Number.isFinite(+sec.marksPerCorrect) ? +sec.marksPerCorrect : 1,
        negativeMarks: Number.isFinite(+sec.negativeMarks) ? +sec.negativeMarks : 0,
      }));
      const perSubjectTotal = sections.reduce((a, sec) => a + (sec.questionCount || 0), 0);
      return {
        subjectId: String(s.subjectId || "").trim(),
        subjectName: String(s.subjectName || "").trim(),
        questionCount: perSubjectTotal,
        sections,
      };
    });

    // Build per-class sections map for payload, ensuring keys exist for selected classes.
    // Empty array = "all sections" for that class.
    const sectionKeysByClass = selClasses.reduce((acc, c) => {
      const arr = Array.isArray(selSectionsByClass[c]) ? selSectionsByClass[c].map(toU) : [];
      acc[c] = arr;
      return acc;
    }, {});

    const flatSectionUnion = Array.from(
      new Set(Object.values(sectionKeysByClass).flat().map(toU))
    );

    const payload = {
      name: form.name.trim(),
      examType: isDescriptive ? "descriptive" : "objective",
      testType: form.testType,
      testTypeLabel: testTypeLabel(form.testType, form.testTypeCustom),
      testTypeCustom: form.testType === "other" ? (form.testTypeCustom || "") : "",
      totalQuestions: +form.totalQuestions,
      totalSubjectQuestions: normalizedSubjects.reduce((a, s) => a + (s.questionCount || 0), 0),
      subjectWise: normalizedSubjects,
      allowNegativeMarking: !!form.allowNegativeMarking,

      // visibility / status
      status: form.status || "draft",
      instructions: form.instructions || "",

      // attempt behavior
      onlineAttemptEnabled: !isDescriptive,
      requiresPdfUpload: isDescriptive,
      pdfMaxSizeMB: isDescriptive ? (+form.pdfMaxSizeMB || 5) : null,
      pdfInstructions: isDescriptive ? (form.pdfInstructions || "") : "",

      randomizeQuestions: !!form.randomizeQuestions,
      allowBackNavigation: !!form.allowBackNavigation,
      showMarksPerQuestion: !!form.showMarksPerQuestion,
      allowedDevices: form.allowedDevices || ["web"],

      // mapping
      classIds: selClasses.slice(0, 40),
      sectionIds: flatSectionUnion,                 // flat union (optional convenience)
      sectionKeysByClass,                           // per-class sections (preferred downstream)
      sectionsByClass: sectionKeysByClass,          // alias for older readers, if any
      branchIds: [],

      createdBy: user?.uid || "",
      createdByUid: user?.uid || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      setSaving(true);
      const ref = await addDoc(collection(db, "schools", schoolId, "examTemplates"), payload);
      setSaving(false);
      if (goToQuestions) {
        nav(`/teacher/exams/templates/${ref.id}/questions`);
      } else {
        nav(`/teacher/exams/templates/${ref.id}`);
      }
    } catch (e) {
      setSaving(false);
      alert(e?.message || "Failed to create template.");
    }
  };

  /* ---------------- UI ---------------- */
  if (schoolLoading || tLoading) {
    return <div className="d-flex align-items-center justify-content-center py-5"><Spinner /></div>;
  }

  const disabled = saving || !isActiveSchool;
  const isDescriptive = form.examType === "descriptive";

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="fw-semibold">
          Create Exam Template (Teacher){" "}
          <Badge bg="light" text="dark">School: {school?.name || "-"}</Badge>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={isActiveSchool ? "success" : "secondary"}>
            {isActiveSchool ? "School Active" : "School Inactive"}
          </Badge>
          <Badge bg={String(form.status).toLowerCase() === "ready" ? "success" : "secondary"}>
            {String(form.status || "draft")}
          </Badge>
        </div>
      </Card.Header>

      <Card.Body>
        {!isActiveSchool && (
          <Alert variant="warning" className="mb-3">
            Your school is not <strong>active</strong>. Activate to create templates.
          </Alert>
        )}

        {(!availableClasses || availableClasses.length === 0) && (
          <Alert variant="info" className="mb-3">
            No class mapping detected for your account. This template will not be visible on the board
            until Admin maps your classes.
          </Alert>
        )}

        {/* ===== Progress ===== */}
        <Row className="g-3 mb-2">
          <Col md={8}>
            <div className="p-3 border rounded-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="fw-semibold">Question Allocation</div>
                <small className="text-muted">{subjectSum} of {+form.totalQuestions || 0}</small>
              </div>
              <ProgressBar now={ratio} label={`${ratio}%`} variant={ratio === 100 ? "success" : "info"} />
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id="mark-ready"
              label="Mark template as Ready"
              disabled={disabled}
              checked={String(form.status).toLowerCase() === "ready"}
              onChange={(e) => setForm((f)=>({ ...f, status: e.target.checked ? "ready" : "draft" }))}
            />
          </Col>
        </Row>

        {/* ===== Basics ===== */}
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Exam Name</Form.Label>
              <Form.Control
                disabled={disabled}
                value={form.name}
                onChange={(e) => setForm((f)=>({ ...f, name: e.target.value }))}
                placeholder="Mid-Term – Mathematics"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Exam Type</Form.Label>
              <Form.Select
                disabled={disabled}
                value={form.examType}
                onChange={(e) => {
                  const nextType = e.target.value === "descriptive" ? "descriptive" : "objective";
                  setForm((f)=>({
                    ...f,
                    examType: nextType,
                    randomizeQuestions: nextType !== "descriptive",
                    showMarksPerQuestion: true,
                  }));
                }}
              >
                <option value="objective">Objective (online attempt)</option>
                <option value="descriptive">Descriptive (PDF answer upload)</option>
              </Form.Select>
              <div className="mt-2">
                {isDescriptive ? (
                  <Badge bg="warning" text="dark">PDF upload required</Badge>
                ) : (
                  <Badge bg="success">Online attempt</Badge>
                )}
              </div>
            </Form.Group>
          </Col>

          <Col md={3} className="d-flex align-items-end">
            <Form.Group className="w-100">
              <Form.Check
                type="switch"
                id="neg-marking"
                label="Enable Negative Marking"
                disabled={disabled}
                checked={!!form.allowNegativeMarking}
                onChange={(e) => setForm((f)=>({ ...f, allowNegativeMarking: e.target.checked }))}
              />
              <Form.Text muted>
                If off, all imported questions will use <b>0</b> negative marks.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Test Type</Form.Label>
              <Form.Select
                disabled={disabled}
                value={form.testType}
                onChange={(e) => setForm((f)=>({ ...f, testType: e.target.value }))}
              >
                {TEST_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              {form.testType === "other" && (
                <Form.Control
                  className="mt-2"
                  placeholder="Enter custom test type (e.g., 'Sunday Test')"
                  value={form.testTypeCustom}
                  onChange={(e) => setForm((f)=>({ ...f, testTypeCustom: e.target.value }))}
                  disabled={disabled}
                />
              )}
            </Form.Group>
          </Col>

          {/* Totals */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Total Questions</Form.Label>
              <Form.Control
                disabled={disabled}
                inputMode="numeric"
                value={form.totalQuestions}
                onChange={(e) => setForm((f)=>({ ...f, totalQuestions: e.target.value.replace(/[^0-9]/g, "") }))}
              />
              <Form.Text muted>
                Sections sum: <strong>{subjectSum}</strong>{" "}
                {subjectSum !== +form.totalQuestions && (
                  <Badge bg="warning" text="dark" className="ms-2">Mismatch</Badge>
                )}
              </Form.Text>
            </Form.Group>
          </Col>

          {/* Descriptive-only */}
          {isDescriptive && (
            <>
              <Col xs={12}><hr className="my-2" /></Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Max PDF Size (MB)</Form.Label>
                  <Form.Control
                    disabled={disabled}
                    inputMode="numeric"
                    value={form.pdfMaxSizeMB}
                    onChange={(e) => setForm((f)=>({ ...f, pdfMaxSizeMB: e.target.value.replace(/[^0-9]/g, "") }))}
                    placeholder="5"
                  />
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group>
                  <Form.Label>PDF Upload Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    disabled={disabled}
                    value={form.pdfInstructions}
                    onChange={(e) => setForm((f)=>({ ...f, pdfInstructions: e.target.value }))}
                    placeholder="Scan pages clearly, merge into a single PDF, then upload."
                  />
                </Form.Group>
              </Col>
            </>
          )}

          <Col xs={12}><hr className="my-3" /></Col>

          {/* ===== Classes & Sections (multi) ===== */}
          <Col xs={12}>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="m-0 fw-semibold">Classes & Sections</Form.Label>
              <Badge bg="light" text="dark" className="border">
                {selClasses.length} class{selClasses.length === 1 ? "" : "es"} selected
              </Badge>
            </div>
            {(!availableClasses || availableClasses.length === 0) ? (
              <div className="text-muted small mt-2">No classes available.</div>
            ) : (
              <Row className="g-2 mt-2">
                {availableClasses.map((c) => (
                  <Col md={6} key={c}>
                    <Card className={`border-0 shadow-sm ${isClassSelected(c) ? "" : "opacity-75"}`}>
                      <Card.Header className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check
                            type="checkbox"
                            id={`cls-${c}`}
                            checked={isClassSelected(c)}
                            onChange={() => toggleClass(c)}
                            disabled={disabled}
                          />
                          <span className="fw-semibold">{c}</span>
                        </div>
                        <div>
                          <Badge bg="light" text="dark" className="border">
                            {Array.isArray(selSectionsByClass[c]) && selSectionsByClass[c].length > 0
                              ? `${selSectionsByClass[c].length} section${selSectionsByClass[c].length === 1 ? "" : "s"}`
                              : "All sections"}
                          </Badge>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {!isClassSelected(c) ? (
                          <div className="text-muted small">Select class to choose sections.</div>
                        ) : (
                          <>
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => clearSections(c)}
                                disabled={disabled}
                                title="Clear selection to allow all sections"
                              >
                                Clear (All Sections)
                              </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              {classSectionPool(c).map((s) => (
                                <Form.Check
                                  key={`${c}-${s}`}
                                  type="checkbox"
                                  id={`${c}-${s}`}
                                  label={s}
                                  checked={isSectionSelected(c, s)}
                                  onChange={() => toggleSection(c, s)}
                                  disabled={disabled}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>

          <Col xs={12}><hr className="my-3" /></Col>

          {/* Subject-wise Distribution */}
          <Col xs={12}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="m-0 fw-semibold">Subject-wise Distribution</Form.Label>
              <Button disabled={disabled} size="sm" variant="outline-primary" onClick={addSubjectRow}>
                + Add Subject
              </Button>
            </div>

            {form.subjectWise.map((row) => {
              const perSubjectTotal = (row.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
              return (
                <Card key={row.id} className="mb-3 shadow-sm">
                  <Card.Header className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1">
                      <div className="small text-muted">Subject</div>
                      <div className="fw-semibold">{row.subjectName || "Untitled"}</div>
                    </div>
                    <Badge bg="light" text="dark">Subject Total: {perSubjectTotal}</Badge>
                    {form.subjectWise.length > 1 && (
                      <Button
                        disabled={disabled}
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeSubjectRow(row.id)}
                        title="Remove subject"
                      >
                        ✕
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2 align-items-end">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Subject Name</Form.Label>
                          <Form.Control
                            disabled={disabled}
                            value={row.subjectName}
                            onChange={(e) => updateSubjectRow(row.id, { subjectName: e.target.value })}
                            placeholder="Physics"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Subject ID (optional)</Form.Label>
                          <Form.Control
                            disabled={disabled}
                            value={row.subjectId}
                            onChange={(e) => updateSubjectRow(row.id, { subjectId: e.target.value })}
                            placeholder="physics-12"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={2} className="d-flex align-items-end">
                        <Button
                          disabled={disabled}
                          size="sm"
                          variant="outline-success"
                          className="ms-auto"
                          onClick={() => addSection(row.id)}
                        >
                          + Add Section
                        </Button>
                      </Col>

                      <Col xs={12}><hr className="my-2" /></Col>

                      {(row.sections || []).map((sec) => (
                        <Row key={sec.id} className="g-2 align-items-end mb-2">
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small mb-1">Key</Form.Label>
                              <Form.Control
                                disabled={disabled}
                                value={sec.key}
                                onChange={(e) => {
                                  const newKey = toU(e.target.value).slice(0, 3);
                                  const syncLabel = !sec.label || toU(sec.label) === toU(sec.key || "");
                                  updateSection(row.id, sec.id, { key: newKey, ...(syncLabel ? { label: newKey } : {}) });
                                }}
                                placeholder="A"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small mb-1">Label (optional)</Form.Label>
                              <Form.Control
                                disabled={disabled}
                                value={sec.label}
                                onChange={(e) => updateSection(row.id, sec.id, { label: e.target.value })}
                                placeholder="A"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small mb-1">Questions</Form.Label>
                              <Form.Control
                                disabled={disabled}
                                inputMode="numeric"
                                value={sec.questionCount ?? ""}
                                onChange={(e) => updateSection(row.id, sec.id, {
                                  questionCount: e.target.value.replace(/[^0-9]/g, ""),
                                })}
                                placeholder="10"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small mb-1">Marks / Correct</Form.Label>
                              <Form.Control
                                disabled={disabled}
                                inputMode="numeric"
                                value={sec.marksPerCorrect ?? 1}
                                onChange={(e) => updateSection(row.id, sec.id, {
                                  marksPerCorrect: e.target.value.replace(/[^0-9.]/g, ""),
                                })}
                                placeholder="1"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small mb-1">Negative Marks</Form.Label>
                              <Form.Control
                                disabled={disabled}
                                inputMode="numeric"
                                value={sec.negativeMarks ?? 0}
                                onChange={(e) => updateSection(row.id, sec.id, {
                                  negativeMarks: e.target.value.replace(/[^0-9.]/g, ""),
                                })}
                                placeholder="0"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={1} className="text-end">
                            {(row.sections || []).length > 1 && (
                              <Button
                                disabled={disabled}
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeSection(row.id, sec.id)}
                                title="Remove section"
                              >
                                ✕
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </Col>

          <Col xs={12}><hr className="my-3" /></Col>

          {/* Behavior toggles */}
          <Col md={4}>
            <Form.Check
              type="switch"
              id="rand-qs"
              label="Randomize Questions"
              disabled={disabled || isDescriptive}
              checked={form.randomizeQuestions}
              onChange={(e) => setForm((f)=>({ ...f, randomizeQuestions: e.target.checked }))}
            />
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="back-nav"
              label="Allow Back Navigation"
              disabled={disabled}
              checked={form.allowBackNavigation}
              onChange={(e) => setForm((f)=>({ ...f, allowBackNavigation: e.target.checked }))}
            />
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="show-marks"
              label="Show Marks/Question (display only)"
              disabled={disabled}
              checked={form.showMarksPerQuestion}
              onChange={(e) => setForm((f)=>({ ...f, showMarksPerQuestion: e.target.checked }))}
            />
          </Col>

          <Col xs={12}>
            <Form.Group className="mt-2">
              <Form.Label>Instructions (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                disabled={disabled}
                value={form.instructions}
                onChange={(e) => setForm((f)=>({ ...f, instructions: e.target.value }))}
                placeholder="Read all questions carefully..."
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>

      {/* Sticky footer actions */}
      <div className="position-sticky bottom-0 bg-body border-top p-3">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Button variant="primary" disabled={!canSave || saving} onClick={() => doCreate(false)}>
            {saving ? "Creating..." : "Create Template"}
          </Button>

          <Button
            variant="success"
            disabled={!canSave || saving}
            onClick={() => doCreate(true)}
            title="Create and go add questions"
          >
            {saving ? "Creating..." : "Create & Add Questions"}
          </Button>

          <Button
            variant="outline-secondary"
            disabled={saving}
            onClick={() => { setForm(DEFAULT_FORM); setSelClasses(availableClasses || []); setSelSectionsByClass({}); }}
          >
            Reset
          </Button>

          <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
            <Badge bg={subjectSum === +form.totalQuestions && +form.totalQuestions > 0 ? "success" : "warning"}>
              Sections Sum: {subjectSum} / Total: {+form.totalQuestions || 0}
            </Badge>
            <Badge bg="info" title={form.testType === "other" ? form.testTypeCustom : testTypeLabel(form.testType)}>
              Test Type: {testTypeLabel(form.testType, form.testTypeCustom)}
            </Badge>
            <Badge bg="light" text="dark" className="border">
              Classes: {selClasses.length || 0}
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );
}
